import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";
import "../signature/signature.css";

import DateRangePicker from "../signature/DateRangePicker";
import { GetCountOfCIR } from "../../../interfaces/Participant";
import { useAuth } from "../../../firebase/AuthProvider";
import { GetCountOfCIRAsync } from "../../../services/participantServices/ParticipantService";
import ExternalFileRequests from "./ExternalFileRequests";
import ExternalFileRequestModal from "../taskPage/Modals/ExternalFileRequestModal";
import Pagination from "../../ui/Pagination/Pagination";
import { PAGINATION } from "../../../Data/SD";

interface DropdownPosition {
  top: number;
  left: number;
}

export default function ExternalFileRequestHeader() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventKeyFromQuery = params.get("eventkey");
  const { currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState(
    eventKeyFromQuery === "sent" ? "sent" : "inbox"
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [count, setCount] = useState<GetCountOfCIR>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
  const [showExternalFileUploadModal, setShowExternalFileUploadModal] =
    useState(false);
  const taskId = params.get("taskId") || "0";

  // Common pagination state
  const [pagination, setPagination] = useState({
    pageNumber: PAGINATION.PageNumber,
    pageSize: PAGINATION.PageSize,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  // Dynamic status options based on active tab

  // Inbox tab doesn't show status dropdown (only shows uploaded status)
  // Sent tab shows all statuses
  // Note: "Approved" is displayed but maps to "Accepted" for backend
  const sentStatusOptions = [
    "All Status",
    "Pending",
    "Accepted", // Display as "Approved" but backend value is "Accepted"
    "Uploaded",
    "Rejected",
    "Revoked",
    "Expired",
  ];

  const [statusOptions] = useState<string[]>(sentStatusOptions);
  
  // Reset status filter when tab changes
  useEffect(() => {
    setStatusFilter("All Status");
  }, [activeTab]);

  // Fetch signature count on component mount
  useEffect(() => {
    currentUser?.getIdToken().then((token) => {
      if (token) {
        getCountOfCIR(currentUser.uid, token);
      }
    });
  }, [currentUser]);

  const getCountOfCIR = (userId: string, token: string) => {
    GetCountOfCIRAsync(token, userId).then((res) => {
      if (res.success) {
        setCount(res.data);
      }
    });
  };

  const handleClick = () => {
    if (!isOpen && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const parentRect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom - parentRect.top + 2,
        left: buttonRect.left - parentRect.left,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option: string) => {
    setStatusFilter(option);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const tabs = [
    { id: "sent", label: "Sent", count: null },
    {
      id: "inbox",
      label: "Inbox",
      count: inboxCount || 0,
    },
  ] as const;

  const navigate = useNavigate();
  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
    // Reset to page 1 when date range changes
    setPagination((prev) => ({ ...prev, pageNumber: PAGINATION.PageNumber }));
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: PAGINATION.PageNumber }));
  }, [searchTerm]);

  // Reset to page 1 when status filter changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: PAGINATION.PageNumber }));
  }, [statusFilter]);

  // Reset to page 1 when tab changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: PAGINATION.PageNumber }));
  }, [activeTab]);

  const handlePageChange = (pageNum: number) => {
    setPagination((prev) => ({
      ...prev,
      pageNumber: pageNum,
    }));
  };

  const handleResetPageSize = (pageNumber: number, pageSize: number) => {
    setPagination({
      pageNumber: pageNumber,
      pageSize: pageSize,
    });
  };

  const handleTotalCountChange = (count: number) => {
    setTotalCount(count);
    // Track inbox count separately
    if (activeTab === "inbox") {
      setInboxCount(count);
    }
  };

  const totalPages = Math.ceil(totalCount / pagination.pageSize);

  return (
    <>
    <div className="drive-context-container">
      <div className="top-bar">
        <div className="breadcrumbs">
          <span
            className="cursor-pointer text-[#7e57c2]"
            onClick={() => navigate("/home")}
          >
            Dashboard
          </span>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-active text-[#364153]">
            Files For Review
          </span>
        </div>
      </div>

      <div className="signature-document-wrapper">
        {/* Header with Tabs in Middle */}
        <header className="signature-main-header">
        

          {/* Tabs in Middle */}
          <div
            className="flex max-w-[16.375rem] h-[2.5rem] rounded-[0.5rem]  items-center justify-center gap-2.5 px-[26px] py-3 relative bg-[#f9f6ff]  border border-solid border-[#efe9fa]"
            data-model-id="459:2733"
            role="tablist"
            aria-label="Message tabs"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`relative h-9 mt-[-10.00px] mb-[-10.00px] rounded-lg ${
                  tab.id === "sent"
                    ? "w-[124px] ml-[-25.00px]"
                    : "w-[126px] mr-[-25.00px]"
                } ${
                  activeTab === tab.id
                    ? "bg-white active shadow-[0px_1px_2px_#0000000f] text-[#7E57C2]"
                    : "bg-transparent text-[#747474]"
                }`}
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
                type="button"
              >
                <div className="flex items-center gap-2 justify-center ">
                  <span className="">{tab.label}</span>

                  {tab.count !== null && tab.count > 0 && (
                    <span
                      className={`count-tab  ${
                        activeTab === tab.id ? "active:" : "not-active"
                      }`}
                      aria-label={`${tab.count} unread messages `}
                    >
                      <span
                        className={`count-tab-text  ${
                          activeTab === tab.id ? "active:" : "not-active"
                        }`}
                      >
                        {tab.count}
                      </span>
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
            {/* Search Input */}
            <div className="signature-search-field">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="19"
              height="19"
              viewBox="0 0 19 19"
              fill="none"
              className="signature-search-icon mb-[2px]"
            >
              <path
                d="M16.1676 16.168L12.8262 12.8266"
                stroke="#9CA3AF"
                strokeWidth="1.15486"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.46782 14.6282C11.8695 14.6282 14.6271 11.8706 14.6271 8.46892C14.6271 5.06727 11.8695 2.30969 8.46782 2.30969C5.06617 2.30969 2.30859 5.06727 2.30859 8.46892C2.30859 11.8706 5.06617 14.6282 8.46782 14.6282Z"
                stroke="#9CA3AF"
                strokeWidth="1.15486"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Requests"
              className="signature-search-input"
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap ml-auto">
            {/* Status Filter - Only show for sent tab */}
            {activeTab === "sent" && (
            <div className="signature-status-dropdown-wrapper" ref={dropdownRef}>
              <button
                ref={buttonRef}
                className="signature-status-button"
                data-model-id="459:2256"
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                type="button"
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-label="Filter by status"
              >
                <div className="signature-status-button-text">
                  {statusFilter}
                </div>

                <img
                  className={` w-[18px] h-3.5 transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  alt=""
                  src="https://c.animaapp.com/M9B8e3bP/img/icon.svg"
                  aria-hidden="true"
                />
              </button>

              {isOpen && (
                <div 
                  className="signature-status-dropdown"
                  style={{
                    position: 'absolute',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }}
                >
                  {statusOptions.map((option) => (
                    <button
                      key={option}
                      className={`signature-status-option ${
                        statusFilter === option ? "signature-status-option-active" : ""
                      }`}
                      onClick={() => handleOptionClick(option)}
                      type="button"
                    >
                      <div className="signature-status-option-text">
                        {option}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}
            <DateRangePicker
              onDateRangeChange={handleDateRangeChange}
              initialStartDate={startDate}
              initialEndDate={endDate}
            />
            {/* <button
              className="btn btn-primary submit-btn"
              onClick={() => setShowExternalFileUploadModal(true)}
            >
              Add Request
            </button> */}
          </div>
        </header>

        {/* Tab Content */}
        <div className="signature-tab-content">
          <ExternalFileRequests
            activeTab={activeTab}
            startDate={startDate}
            endDate={endDate}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            pagination={pagination}
            onTotalCountChange={handleTotalCountChange}
          />
        </div>

        {/* Common Pagination */}
        {totalCount > 0 && (
          <div
            style={{
              marginTop: "1rem",
              padding: " 0 1.5rem",
              borderTop: "1.155px solid #E5E7EB",
            }}
          >
            <Pagination
              currentPage={pagination.pageNumber}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              pageSize={pagination.pageSize}
              resetPageSize={handleResetPageSize}
            />
          </div>
        )}
      </div>
      {showExternalFileUploadModal && (
        <ExternalFileRequestModal
          showExternalFileReqeustModal={showExternalFileUploadModal}
          handleCloseExternalFileReqeustModal={() =>
            setShowExternalFileUploadModal(false)
          }
          taskId={Number(taskId) || 0}
        />
      )}
      </div>
    </>
  );
}
